/**
 * Module 3.1 - Registration > Citizen
 * Shared constants and options for all entities
 * I18n-aware options with {pt, en, es} labels
 */

export interface I18nLabel {
    pt: string;
    en: string;
    es: string;
}

export interface I18nOption<T = string> {
    label: I18nLabel;
    value: T;
}

/** Helper to build a tuple list from I18nOption[] for backward compat */
const toTuples = (options: I18nOption[], lang: 'pt' | 'en' | 'es' = 'pt'): [string, string][] =>
    options.map((o) => [o.value, o.label[lang]]);

// Citizens - Citizenship Status Options
export const citizenshipStatusI18nOptions: I18nOption[] = [
    { label: { pt: 'Ativo', en: 'Active', es: 'Activo' }, value: 'ACTIVE' },
    { label: { pt: 'Suspenso', en: 'Suspended', es: 'Suspendido' }, value: 'SUSPENDED' },
    { label: { pt: 'Verificado', en: 'Verified', es: 'Verificado' }, value: 'VERIFIED' },
    { label: { pt: 'Pendente', en: 'Pending', es: 'Pendiente' }, value: 'PENDING' },
];

// CitizenPreferences - Preference Type Options
export const preferenceTypeI18nOptions: I18nOption[] = [
    { label: { pt: 'Interface', en: 'Interface', es: 'Interfaz' }, value: 'interface' },
    { label: { pt: 'Comunicacao', en: 'Communication', es: 'Comunicacion' }, value: 'communication' },
    { label: { pt: 'Notificacao', en: 'Notification', es: 'Notificacion' }, value: 'notification' },
    { label: { pt: 'Privacidade', en: 'Privacy', es: 'Privacidad' }, value: 'privacy' },
    { label: { pt: 'Interesse', en: 'Interest', es: 'Interes' }, value: 'interest' },
    { label: { pt: 'Agenda', en: 'Schedule', es: 'Agenda' }, value: 'schedule' },
    { label: { pt: 'Consentimento', en: 'Consent', es: 'Consentimiento' }, value: 'consent' },
];

// PublicAgencyFollows - Follow Status Options
export const followStatusI18nOptions: I18nOption[] = [
    { label: { pt: 'Ativo', en: 'Active', es: 'Activo' }, value: 'active' },
    { label: { pt: 'Pausado', en: 'Paused', es: 'Pausado' }, value: 'paused' },
    { label: { pt: 'Bloqueado', en: 'Blocked', es: 'Bloqueado' }, value: 'blocked' },
    { label: { pt: 'Silenciado', en: 'Muted', es: 'Silenciado' }, value: 'muted' },
];

// PublicAgencyFollows - Content Types Options
export const contentTypesI18nOptions: I18nOption[] = [
    { label: { pt: 'Noticias', en: 'News', es: 'Noticias' }, value: 'news' },
    { label: { pt: 'Eventos', en: 'Events', es: 'Eventos' }, value: 'events' },
    { label: { pt: 'Licitacoes', en: 'Tenders', es: 'Licitaciones' }, value: 'tenders' },
    { label: { pt: 'Documentos', en: 'Documents', es: 'Documentos' }, value: 'documents' },
    { label: { pt: 'Todos', en: 'All', es: 'Todos' }, value: 'all' },
];

// Shared status options
export const activeStatusI18nOptions: I18nOption[] = [
    { label: { pt: 'Ativo', en: 'Active', es: 'Activo' }, value: 'true' },
    { label: { pt: 'Inativo', en: 'Inactive', es: 'Inactivo' }, value: 'false' },
];

export const yesNoI18nOptions: I18nOption[] = [
    { label: { pt: 'Sim', en: 'Yes', es: 'Si' }, value: 'true' },
    { label: { pt: 'Nao', en: 'No', es: 'No' }, value: 'false' },
];

// ---------------------------------------------------------------------------
// Backwards-compatible tuple exports (deprecated - use I18nOption variants)
// ---------------------------------------------------------------------------
/** @deprecated Use citizenshipStatusI18nOptions */
export const CITIZENSHIP_STATUS_OPTIONS: [string, string][] = toTuples(citizenshipStatusI18nOptions);
/** @deprecated Use preferenceTypeI18nOptions */
export const PREFERENCE_TYPE_OPTIONS: [string, string][] = toTuples(preferenceTypeI18nOptions);
/** @deprecated Use followStatusI18nOptions */
export const FOLLOW_STATUS_OPTIONS: [string, string][] = toTuples(followStatusI18nOptions);
/** @deprecated Use contentTypesI18nOptions */
export const CONTENT_TYPES_OPTIONS: [string, string][] = toTuples(contentTypesI18nOptions);
/** @deprecated Use activeStatusI18nOptions */
export const ACTIVE_STATUS_OPTIONS: [string, string][] = toTuples(activeStatusI18nOptions);
/** @deprecated Use yesNoI18nOptions */
export const YES_NO_OPTIONS: [string, string][] = toTuples(yesNoI18nOptions);
